import React, { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import {
  black,
  gray45,
  gray60,
  gray85,
  kleinBlue,
  screenMdMax,
  screenSmMax,
  screenSmMin,
  screenXsMax,
  screenXxsMax,
  spacer16,
  spacer20,
  spacer24,
  spacer32,
  spacer40,
  spacer64,
  spacer8,
  white,
} from '@spotify-internal/encore-foundation';
import { CollapseButton, Type } from '@spotify-internal/encore-web';
import { UnstyledLink } from '../../ui';
import { FaqCategory, HELP_LIST_TRACK_CAT, hidden } from '../util';

import { eventHandler } from '../../Tracking';

const borderRadius = '6px';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: 0;
  margin: 0;
`;

const TopicGridComponent = styled.div`
  justify-content: center;
  background: ${({ theme }) => theme.help.grayBox.background};
  color: ${({ theme }) => theme.help.grayBox.text};
  width: 100%;
  padding: ${spacer40} ${spacer8} ${spacer8} ${spacer40};

  @media (max-width: ${screenSmMax}) {
    padding: ${spacer40} ${spacer16} ${spacer16} ${spacer40};
  }

  @media (max-width: ${screenXsMax}) {
    padding: ${spacer32} 0 ${spacer16} ${spacer16};
    margin: 0;
  }
`;

const Headline = styled(Type.h2).attrs({
  variant: Type.heading2,
  weight: Type.bold,
})<{}>`
  padding: 0 0 ${spacer40} 0;

  @media (max-width: ${screenXsMax}) {
    padding: 0 0 ${spacer32} 0;
  }
`;

const Topics = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-content: flex-start;
  width: 100%;
`;

const Topic = styled(UnstyledLink)`
  display: flex;
  flex-flow: column nowrap;
  border-radius: ${borderRadius};
  background: ${white};
  margin: 0 ${spacer32} ${spacer32} 0;
  padding: 0;
  overflow: hidden;
  width: calc(33.33% - ${spacer32});
  height: 208px;

  @media (max-width: ${screenMdMax}) {
    margin: 0 ${spacer24} ${spacer24} 0;
    width: calc(50% - ${spacer24});
  }

  @media (max-width: ${screenSmMax}) {
    height: 176px;
  }

  @media (max-width: ${screenXsMax}) {
    width: calc(100% - ${spacer16});
    margin: 0 0 ${spacer16} 0;
  }

  &:hover {
    cursor: pointer;
    color: unset;
  }

  &:hover h3 {
    color: ${gray45};
  }
`;

const TopicName = styled(Type.h3).attrs({
  variant: Type.heading3,
})`
  flex: 0;
  padding: ${spacer20} ${spacer20} 0 ${spacer20};
  color: ${kleinBlue};
  overflow: hidden;
  min-height: 58px;
`;

const TopicContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  height: 100%;

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}),
    (min-width: 0) and (max-width: ${screenXxsMax}) {
    display: block;
    padding-right: ${spacer20};
  }
`;

const TopicTextContainer = styled.div`
  flex: 3;
  padding: 0 0 0 ${spacer20};
  max-height: 112px;
`;

const TopicIconContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  max-width: 132px;

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}),
    (min-width: 0) and (max-width: ${screenXxsMax}) {
    ${hidden}
  }
`;

const TopicCount = styled(Type.p).attrs({
  variant: Type.body2,
})`
  color: ${gray60};
  padding-bottom: 8px;
`;

const TopicDescription = styled(Type.p).attrs({
  variant: Type.body1,
})``;

const TopicIcon = styled.img`
  transform: rotate(25deg) translate(20px);
`;

const CTA = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${spacer64};
  background-color: ${gray85};

  &:hover {
    cursor: pointer;
  }
`;

const CTAText = styled(Type).attrs({
  variant: Type.cta2,
})`
  padding-bottom: 0;
  text-transform: uppercase;
  color: ${black};
`;

export type TopicGridType = {
  headline?: string;
  items?: FaqCategory[];
  initialCount?: number;
};

export const TopicGrid: React.FC<TopicGridType> = ({
  headline = 'Browse by topic',
  items = [],
  initialCount = 6,
}) => {
  const [displayAll, setDisplayAll] = useState(false);
  const ctaText = `See ${displayAll ? 'less' : 'more'}`;

  const itemsToDisplay = displayAll ? items : items.slice(0, initialCount);

  const ctaOnClick = (e: MouseEvent<HTMLDivElement>) => {
    eventHandler(
      `Frodor - ${HELP_LIST_TRACK_CAT}`,
      `Topics CTA - ${ctaText}`,
    )(e);
    setDisplayAll(!displayAll);
  };

  return (
    <Container>
      <TopicGridComponent data-testid="help-topics">
        <Headline>{headline}</Headline>
        <Topics>
          {itemsToDisplay.map((item, key) => (
            <Topic
              key={key}
              track={[HELP_LIST_TRACK_CAT, `Topics Link - ${item.name}`]}
              href={`help/${item.urlSlug}`}
            >
              <TopicName>{item.name}</TopicName>
              <TopicContentContainer>
                <TopicTextContainer>
                  <TopicCount>{item.faqs?.length || 0} questions</TopicCount>
                  <TopicDescription>{item.description || ''}</TopicDescription>
                </TopicTextContainer>
                <TopicIconContainer>
                  {item.icon && (
                    <TopicIcon src={`${item.icon.file.url}?fm=png&fl=png8`} />
                  )}
                </TopicIconContainer>
              </TopicContentContainer>
            </Topic>
          ))}
        </Topics>
      </TopicGridComponent>
      {items.length > initialCount ? (
        <CTA onClick={ctaOnClick}>
          <CollapseButton expanded={displayAll}>
            <CTAText>{ctaText}</CTAText>
          </CollapseButton>
        </CTA>
      ) : null}
    </Container>
  );
};
